import React, { useEffect, useState } from 'react';
import { useAuth } from '../context/AuthContext';
import VendorLayout from '../components/VendorLayout';
import axios from 'axios';
import { 
  ClockIcon, 
  CheckCircleIcon, 
  CurrencyDollarIcon,
  CalendarIcon,
  BuildingOfficeIcon
} from '@heroicons/react/24/solid';

interface Issue {
  id: string;
  title: string;
  description: string;
  status: string;
  priority: string;
  createdAt: string;
  updatedAt: string;
}

interface Payment {
  id: string;
  amount: number;
  status: string;
  description: string;
  createdAt: string;
  updatedAt: string;
}

interface VendorService {
  id: string;
  name: string;
  description?: string;
  hourlyRate?: number;
}

const VendorDashboard: React.FC = () => {
  const { user } = useAuth();
  const [activeSection, setActiveSection] = useState('jobs');
  const [openRequests, setOpenRequests] = useState<Issue[]>([]);
  const [completedJobs, setCompletedJobs] = useState<Issue[]>([]);
  const [payments, setPayments] = useState<Payment[]>([]);
  const [services, setServices] = useState<VendorService[]>([]);
  const [loading, setLoading] = useState(true);
  const [newService, setNewService] = useState({
    name: '',
    description: '',
    hourlyRate: ''
  });

  useEffect(() => {
    const fetchData = async () => {
      setLoading(true);
      try {
        // Fetch issues assigned to this vendor
        const issuesRes = await axios.get<Issue[]>('/api/issues', {
          params: { vendorId: user?.id, hoaId: user?.hoaId }
        });
        setOpenRequests(issuesRes.data.filter(i => i.status !== 'RESOLVED' && i.status !== 'CLOSED'));
        setCompletedJobs(issuesRes.data.filter(i => i.status === 'RESOLVED' || i.status === 'CLOSED'));

        // Fetch payments for this vendor
        const paymentsRes = await axios.get<Payment[]>('/api/payments', {
          params: { vendorId: user?.id, hoaId: user?.hoaId }
        });
        setPayments(paymentsRes.data);

        // Fetch vendor's services
        const servicesRes = await axios.get<VendorService[]>(`/api/vendors/${user?.id}/services`, {
          headers: {
            Authorization: `Bearer ${user?.token}`
          }
        });
        setServices(servicesRes.data);
      } catch (err) {
        console.error('Error fetching vendor data:', err);
      } finally {
        setLoading(false);
      }
    };
    if (user?.id) fetchData();
  }, [user]);

  const getPriorityColor = (priority: string) => {
    switch (priority?.toLowerCase()) {
      case 'high': return 'text-red-400 bg-red-500/20';
      case 'medium': return 'text-yellow-400 bg-yellow-500/20';
      case 'low': return 'text-green-400 bg-green-500/20';
      default: return 'text-blue-400 bg-blue-500/20';
    }
  };

  const getStatusColor = (status: string) => {
    switch (status?.toLowerCase()) {
      case 'pending': return 'text-yellow-400 bg-yellow-500/20';
      case 'in_progress': return 'text-blue-400 bg-blue-500/20';
      case 'resolved': return 'text-green-400 bg-green-500/20';
      case 'paid': return 'text-green-400 bg-green-500/20';
      default: return 'text-gray-400 bg-gray-500/20';
    }
  };

  const renderActiveJobs = () => (
    <div className="p-6">
      <div className="flex items-center justify-between mb-6">
        <h3 className="text-xl font-semibold text-white flex items-center">
          <ClockIcon className="w-6 h-6 mr-2 text-blue-400" />
          Active Work Orders
        </h3>
        <span className="bg-blue-500/20 text-blue-300 px-3 py-1 rounded-full text-sm">
          {openRequests.length} active
        </span>
      </div>

      {loading ? (
        <div className="flex justify-center py-12">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-400"></div>
        </div>
      ) : openRequests.length === 0 ? (
        <div className="text-center py-12">
          <CheckCircleIcon className="w-16 h-16 text-gray-500 mx-auto mb-4" />
          <p className="text-gray-400 text-lg">No active work orders</p>
          <p className="text-gray-500 text-sm">All caught up! Check back later for new assignments.</p>
        </div>
      ) : (
        <div className="grid gap-4">
          {openRequests.map(request => (
            <div key={request.id} className="bg-white/5 backdrop-blur-sm rounded-lg p-5 border border-white/10 hover:border-blue-400/50 transition-all">
              <div className="flex justify-between items-start mb-3">
                <h4 className="text-lg font-semibold text-white">{request.title}</h4>
                <div className="flex space-x-2">
                  <span className={`px-2 py-1 rounded-full text-xs ${getPriorityColor(request.priority)}`}>
                    {request.priority}
                  </span>
                  <span className={`px-2 py-1 rounded-full text-xs ${getStatusColor(request.status)}`}>
                    {request.status.replace('_', ' ')}
                  </span>
                </div>
              </div>
              <p className="text-gray-300 mb-4">{request.description}</p>
              <div className="flex items-center justify-between text-sm">
                <div className="flex items-center text-gray-400">
                  <CalendarIcon className="w-4 h-4 mr-1" />
                  Created {new Date(request.createdAt).toLocaleDateString()}
                </div>
                <button 
                  onClick={() => handleUpdateStatus(request.id)}
                  className="bg-blue-500 hover:bg-blue-600 text-white px-4 py-2 rounded-lg transition-colors"
                >
                  Update Status
                </button>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );

  const renderCompletedWork = () => (
    <div className="p-6">
      <div className="flex items-center justify-between mb-6">
        <h3 className="text-xl font-semibold text-white flex items-center">
          <CheckCircleIcon className="w-6 h-6 mr-2 text-green-400" />
          Completed Work
        </h3>
        <span className="bg-green-500/20 text-green-300 px-3 py-1 rounded-full text-sm">
          {completedJobs.length} completed
        </span>
      </div>

      {loading ? (
        <div className="flex justify-center py-12">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-green-400"></div>
        </div>
      ) : completedJobs.length === 0 ? (
        <div className="text-center py-12">
          <ClockIcon className="w-16 h-16 text-gray-500 mx-auto mb-4" />
          <p className="text-gray-400 text-lg">No completed work yet</p>
          <p className="text-gray-500 text-sm">Completed jobs will appear here once you finish work orders.</p>
        </div>
      ) : (
        <div className="grid gap-4">
          {completedJobs.map(job => (
            <div key={job.id} className="bg-white/5 backdrop-blur-sm rounded-lg p-5 border border-white/10">
              <div className="flex justify-between items-start mb-3">
                <h4 className="text-lg font-semibold text-white">{job.title}</h4>
                <span className="bg-green-500/20 text-green-300 px-2 py-1 rounded-full text-xs">
                  Completed
                </span>
              </div>
              <p className="text-gray-300 mb-4">{job.description}</p>
              <div className="flex items-center justify-between text-sm text-gray-400">
                <div className="flex items-center">
                  <CalendarIcon className="w-4 h-4 mr-1" />
                  Completed {new Date(job.updatedAt).toLocaleDateString()}
                </div>
                <button 
                  onClick={() => handleViewDetails(job.id)}
                  className="text-blue-400 hover:text-blue-300 transition-colors"
                >
                  View Details
                </button>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );

  const renderPayments = () => (
    <div className="p-6">
      <div className="flex items-center justify-between mb-6">
        <h3 className="text-xl font-semibold text-white flex items-center">
          <CurrencyDollarIcon className="w-6 h-6 mr-2 text-yellow-400" />
          Payment History
        </h3>
        <div className="text-right">
          <p className="text-sm text-gray-400">Total Earned</p>
          <p className="text-xl font-bold text-green-400">
            ${payments.reduce((sum, p) => sum + p.amount, 0).toFixed(2)}
          </p>
        </div>
      </div>

      {loading ? (
        <div className="flex justify-center py-12">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-yellow-400"></div>
        </div>
      ) : payments.length === 0 ? (
        <div className="text-center py-12">
          <CurrencyDollarIcon className="w-16 h-16 text-gray-500 mx-auto mb-4" />
          <p className="text-gray-400 text-lg">No payments yet</p>
          <p className="text-gray-500 text-sm">Payment history will appear here once work is invoiced.</p>
        </div>
      ) : (
        <div className="grid gap-4">
          {payments.map(payment => (
            <div key={payment.id} className="bg-white/5 backdrop-blur-sm rounded-lg p-5 border border-white/10">
              <div className="flex justify-between items-start mb-3">
                <div>
                  <h4 className="text-lg font-semibold text-white">${payment.amount.toFixed(2)}</h4>
                  <p className="text-gray-300">{payment.description}</p>
                </div>
                <span className={`px-2 py-1 rounded-full text-xs ${getStatusColor(payment.status)}`}>
                  {payment.status}
                </span>
              </div>
              <div className="flex items-center text-sm text-gray-400">
                <CalendarIcon className="w-4 h-4 mr-1" />
                {new Date(payment.createdAt).toLocaleDateString()}
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );

  const handleAddService = async (e: React.FormEvent) => {
    e.preventDefault();
    try {
      const response = await axios.post<VendorService>(`/api/vendors/${user?.id}/services`, {
        name: newService.name,
        description: newService.description,
        hourlyRate: newService.hourlyRate ? parseFloat(newService.hourlyRate) : undefined
      }, {
        headers: {
          Authorization: `Bearer ${user?.token}`
        }
      });
      setServices([...services, response.data]);
      setNewService({ name: '', description: '', hourlyRate: '' });
    } catch (error) {
      console.error('Error adding service:', error);
    }
  };

  const handleDeleteService = async (serviceId: string) => {
    try {
      await axios.delete(`/api/vendors/${user?.id}/services/${serviceId}`, {
        headers: {
          Authorization: `Bearer ${user?.token}`
        }
      });
      setServices(services.filter(service => service.id !== serviceId));
    } catch (error) {
      console.error('Error deleting service:', error);
    }
  };

  const handleUpdateStatus = async (issueId: string) => {
    try {
      // For now, we'll cycle through statuses: OPEN -> IN_PROGRESS -> RESOLVED
      const currentIssue = openRequests.find(req => req.id === issueId);
      if (!currentIssue) return;

      let newStatus = 'IN_PROGRESS';
      if (currentIssue.status === 'IN_PROGRESS') {
        newStatus = 'RESOLVED';
      }

      await axios.put(`/api/issues/${issueId}`, {
        status: newStatus
      }, {
        headers: {
          Authorization: `Bearer ${user?.token}`
        }
      });

      // Refresh the data
      const issuesRes = await axios.get<Issue[]>('/api/issues', {
        params: { vendorId: user?.id, hoaId: user?.hoaId }
      });
      setOpenRequests(issuesRes.data.filter(i => i.status !== 'RESOLVED' && i.status !== 'CLOSED'));
      setCompletedJobs(issuesRes.data.filter(i => i.status === 'RESOLVED' || i.status === 'CLOSED'));
    } catch (error) {
      console.error('Error updating issue status:', error);
    }
  };

  const handleViewDetails = (jobId: string) => {
    // For now, show a modal or detailed view
    const job = completedJobs.find(j => j.id === jobId);
    if (job) {
      alert(`Job Details:\n\nTitle: ${job.title}\nDescription: ${job.description}\nStatus: ${job.status}\nPriority: ${job.priority}\nCompleted: ${new Date(job.updatedAt).toLocaleDateString()}`);
    }
  };

  const renderServices = () => (
    <div className="p-6">
      <div className="flex items-center justify-between mb-6">
        <h3 className="text-xl font-semibold text-white flex items-center">
          <BuildingOfficeIcon className="w-6 h-6 mr-2 text-blue-400" />
          My Services
        </h3>
        <span className="bg-blue-500/20 text-blue-300 px-3 py-1 rounded-full text-sm">
          {services.length} services
        </span>
      </div>

      {/* Add New Service Form */}
      <div className="bg-white/5 backdrop-blur-sm rounded-lg p-5 border border-white/10 mb-6">
        <h4 className="text-lg font-semibold text-white mb-4">Add New Service</h4>
        <form onSubmit={handleAddService} className="space-y-4">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-300 mb-1">Service Name</label>
              <input
                type="text"
                value={newService.name}
                onChange={(e) => setNewService({...newService, name: e.target.value})}
                className="w-full px-3 py-2 bg-white/10 border border-white/20 rounded-lg text-white placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-blue-500"
                placeholder="e.g., Lawn Mowing"
                required
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-300 mb-1">Hourly Rate ($)</label>
              <input
                type="number"
                value={newService.hourlyRate}
                onChange={(e) => setNewService({...newService, hourlyRate: e.target.value})}
                className="w-full px-3 py-2 bg-white/10 border border-white/20 rounded-lg text-white placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-blue-500"
                placeholder="25.00"
                step="0.01"
              />
            </div>
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-300 mb-1">Description</label>
            <textarea
              value={newService.description}
              onChange={(e) => setNewService({...newService, description: e.target.value})}
              className="w-full px-3 py-2 bg-white/10 border border-white/20 rounded-lg text-white placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-blue-500"
              placeholder="Describe your service..."
              rows={2}
            />
          </div>
          <button
            type="submit"
            className="bg-blue-500 hover:bg-blue-600 text-white px-6 py-2 rounded-lg transition-colors"
          >
            Add Service
          </button>
        </form>
      </div>

      {/* Services List */}
      {loading ? (
        <div className="flex justify-center py-12">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-400"></div>
        </div>
      ) : services.length === 0 ? (
        <div className="text-center py-12">
          <BuildingOfficeIcon className="w-16 h-16 text-gray-500 mx-auto mb-4" />
          <p className="text-gray-400 text-lg">No services added yet</p>
          <p className="text-gray-500 text-sm">Add your first service above to get started.</p>
        </div>
      ) : (
        <div className="grid gap-4">
          {services.map(service => (
            <div key={service.id} className="bg-white/5 backdrop-blur-sm rounded-lg p-5 border border-white/10">
              <div className="flex justify-between items-start mb-3">
                <div>
                  <h4 className="text-lg font-semibold text-white">{service.name}</h4>
                  {service.description && (
                    <p className="text-gray-300 text-sm mt-1">{service.description}</p>
                  )}
                </div>
                <div className="flex items-center space-x-3">
                                     {service.hourlyRate && (
                     <span className="bg-green-500/20 text-green-300 px-2 py-1 rounded-full text-sm">
                       ${Number(service.hourlyRate).toFixed(2)}/hr
                     </span>
                   )}
                  <button
                    onClick={() => handleDeleteService(service.id)}
                    className="text-red-400 hover:text-red-300 transition-colors"
                  >
                    Delete
                  </button>
                </div>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );

  const renderContent = () => {
    switch (activeSection) {
      case 'jobs': return renderActiveJobs();
      case 'completed': return renderCompletedWork();
      case 'services': return renderServices();
      case 'payments': return renderPayments();
      default: return renderActiveJobs();
    }
  };

  return (
    <VendorLayout 
      activeSection={activeSection} 
      onSectionChange={setActiveSection}
    >
      {renderContent()}
    </VendorLayout>
  );
};

export default VendorDashboard; 